import { FeatureFlaggable, ProviderPortalFeatureFlag } from "featureflag";
import { CurrentUserData } from "security";
import {
  hasAuditLogAccess,
  hasClientReportAccess,
  hasContactReadAccess,
  hasOfferHoursAccess,
  hasProviderAccess,
  hasProviderMetricsAccess,
  hasRosterAccess,
} from "./security";
import { WORDPRESS_HOME_URL } from "../layouts/util";

/**
 * Single source of truth for the provider app's feature destinations.
 * Drives three consumers that used to gate independently:
 *  - the left-nav (grouped, alphabetised per group)
 *  - the route guard ({@link PortalFeatureRoute})
 *  - the landing / denial redirect ({@link firstAccessibleRoute})
 */
export type Access = (user: CurrentUserData | null) => boolean;
export type AppGroup = "client" | "business";

/**
 * Flaggable so a destination can be switched off per environment. `canAccess` takes the user and
 * nothing else, and a runtime flag arrives on a React context, so a plain predicate cannot read
 * one. Tagging the entry and filtering the map through {@link useAccessMap} is how the two meet.
 * Build-time flags still compose inside `canAccess`, the way canAccessAnticipatedDischarge does.
 */
export type AppDestination = FeatureFlaggable<{
  key: string;
  label: string; // nav display text
  selection: string; // matches the legacy LeftNavbar `currentSelection` value for active-highlight
  route: string; // in-app path, e.g. '/roster'
  group: AppGroup;
  subGroup?: string; // optional third nav tier, keyed into APP_SUBGROUPS
  icon: string; // FontAwesome classes
  canAccess: Access;
}>;

const isApEnabled = process.env.REACT_APP_AP_ENABLED === "true";

/**
 * Composite gates: a feature token AND a legacy rollout flag. Keeping the flag here
 * (rather than in the nav/route) makes Phase 6 retirement a one-line edit per gate.
 */
export const canAccessOfferAtRisk: Access = (user) =>
  hasClientReportAccess(user) && hasOfferHoursAccess(user);
export const canAccessAnticipatedDischarge: Access = (user) =>
  hasClientReportAccess(user) && isApEnabled;

/**
 * Ordered by **redirect priority** - Client Placement is "home" (index 0). The nav renders
 * this grouped and alphabetised; only the redirect walk relies on the array order.
 */
export const ACCESS_MAP: AppDestination[] = [
  {
    key: "clientPlacement",
    label: "Client Placement",
    selection: "Client Placement",
    route: "/referral",
    group: "client",
    icon: "fa-solid fa-location-dot",
    canAccess: hasProviderAccess,
  },
  {
    key: "clientCare",
    label: "Client Care",
    selection: "Client Care",
    route: "/clients",
    group: "client",
    icon: "fa-solid fa-user-group",
    canAccess: hasClientReportAccess,
  },
  {
    key: "notification",
    label: "Notification",
    selection: "Notification",
    route: "/notifications",
    group: "client",
    icon: "fa-solid fa-bell",
    canAccess: hasClientReportAccess,
  },
  {
    key: "authorizations",
    label: "Authorizations",
    selection: "Authorization",
    route: "/authorizations",
    group: "client",
    icon: "fa-solid fa-paste",
    canAccess: hasClientReportAccess,
  },
  {
    key: "offerAtRisk",
    label: "Client Offer - At Risk",
    selection: "Client Offers - At Risk",
    route: "/clientOfferAtRisk",
    group: "client",
    icon: "fa-solid fa-circle-exclamation",
    canAccess: canAccessOfferAtRisk,
  },
  {
    key: "submittedRequests",
    label: "Submitted Requests",
    selection: "Submitted Requests",
    route: "/submittedRequests",
    group: "client",
    icon: "fa-solid fa-tasks",
    canAccess: hasClientReportAccess,
  },
  {
    key: "anticipatedDischarges",
    label: "Anticipated Discharge Dates",
    selection: "Anticipated Discharges",
    route: "/anticipatedDischarges",
    group: "client",
    icon: "fa-solid fa-user-check",
    canAccess: canAccessAnticipatedDischarge,
  },
  {
    key: "providerMetrics",
    label: "Provider Metrics",
    selection: "Provider Metrics",
    route: "/providerMetrics",
    group: "business",
    icon: "fa-solid fa-square-poll-vertical",
    canAccess: hasProviderMetricsAccess,
  },
  {
    key: "roster",
    label: "Roster Management",
    selection: "Roster Management",
    route: "/roster",
    group: "business",
    icon: "fa-solid fa-table-list",
    canAccess: hasRosterAccess,
  },
  {
    key: "progressPortraitDownloads",
    label: "Progress Portrait",
    selection: "Progress Portrait",
    route: "/audit/progress-portrait-downloads",
    group: "business",
    subGroup: "audit",
    icon: "fa-solid fa-download",
    canAccess: hasAuditLogAccess,
    featureFlag: ProviderPortalFeatureFlag.BHPNPP_3219_PROGRESS_PORTRAIT_DISABLED,
    featureFlagNegate: true,
  },
  // Last: lowest redirect priority. A user holding Audit Log alongside a client-facing token lands
  // on the client-facing page, and only an audit-log-only user lands here.
  //
  // The flag name reads backwards, hence featureFlagNegate: the entry survives while export is on.
  // Retiring the flag means deleting these two properties and nothing else.
  {
    key: "auditExport",
    label: "Export",
    selection: "Audit Export",
    route: "/audit/export",
    group: "business",
    subGroup: "audit",
    icon: "fa-solid fa-file-export",
    canAccess: hasAuditLogAccess,
    featureFlag: ProviderPortalFeatureFlag.TDTL_9900_IS_EXPORT_DISABLED,
    featureFlagNegate: true,
  },
  // Last, because array order is redirect priority and this destination has the lowest.
  // Nearly everyone holds the grant, so anywhere else it would take the landing page from the
  // other destinations.
  //
  // The flag name reads backwards, hence featureFlagNegate: the entry survives while contacts is on.
  {
    key: "businessContacts",
    label: "Contact Management",
    selection: "Contact Management",
    route: "/contacts",
    group: "business",
    icon: "fa-solid fa-address-book",
    canAccess: hasContactReadAccess,
    featureFlag: ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED,
    featureFlagNegate: true,
  },
];

/** Nav group rendering order, labels and header icons. */
export const APP_GROUPS: { key: AppGroup; label: string; icon: string }[] = [
  { key: "client", label: "Client Management", icon: "fa-solid fa-users" },
  {
    key: "business",
    label: "Business Management",
    icon: "fa-solid fa-briefcase",
  },
];

/** Sub-group rendering order, labels and icons. Headers only - no route, no page; each folds its own
 *  children the way a group header does. */
export const APP_SUBGROUPS: { key: string; label: string; icon: string }[] = [
  { key: "audit", label: "Audit Log", icon: "fa-solid fa-clipboard-check" },
];

/** Where a user with no reachable feature is sent: the WordPress marketing home (BHPNPP-3108 #6),
 *  the same URL the Catalight logo links to. Replaces the google.com shim. */
export const NO_ACCESS_REDIRECT_URL = WORDPRESS_HOME_URL;

/**
 * First reachable in-app route in priority order, or null when the user can reach nothing.
 *
 * Takes the map rather than reading {@link ACCESS_MAP} directly, so callers pass the flag-pruned
 * one from {@link useAccessMap}. A destination the flag removed must not be offered as a landing
 * page.
 */
export const firstAccessibleRoute = (
  user: CurrentUserData | null,
  map: Readonly<AppDestination[]>,
): string | null =>
  map.find((destination) => destination.canAccess(user))?.route ?? null;
